import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ShoppingCart, CheckCircle, Eye, Tag, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import { formatCurrency, fmtNum } from '@/utils/currency';
import POViewer from '@/components/POViewer';
import CurrencySymbol from '@/components/CurrencySymbol';
import { SearchFilter } from '@/components/SearchFilter';

const STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'completed', label: 'Completed' },
];

export default function PurchaseOrders() {
  const { user, token } = useContext(AuthContext);
  
  // Permission checks
  const canCreatePO = user?.permissions?.includes('purchase_orders') || user?.permissions?.includes('edit_po') || user?.permissions?.includes('all');
  const canEditPO = user?.permissions?.includes('edit_po') || user?.permissions?.includes('all');
  const canDelete = user?.role === 'Super Admin' || user?.role === 'Admin';
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [showPODialog, setShowPODialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [viewingPO, setViewingPO] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingPO, setEditingPO] = useState(null);
  const [poFormData, setPOFormData] = useState({
    supplier_name: '',
    date: new Date().toISOString().split('T')[0],
    items: [],
    category_id: '',
    notes: '',
    tax: 0,
    currency: 'USD',
    advance_paid: 0
  });
  const [newItem, setNewItem] = useState({
    item_name: '',
    description: '',
    quantity: 1,
    unit: 'pcs',
    price: 0
  });
  const [customUnits, setCustomUnits] = useState([]);
  const [addingUnit, setAddingUnit] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const DEFAULT_UNITS = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'lbs', label: 'Pounds' },
    { value: 'box', label: 'Box' },
    { value: 'pack', label: 'Pack' },
    { value: 'set', label: 'Set' },
  ];

  useEffect(() => {
    fetchPurchaseOrders();
    fetchCategories();
    fetchSuppliers();
    fetchExchangeRates();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseOrders(response.data);
      if (activeTab === 'all') setFilteredPOs(response.data);
      else setFilteredPOs(response.data.filter(po => po.status === activeTab));
    } catch (error) {
      toast.error('Failed to load purchase orders');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to load suppliers');
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRates(response.data.exchange_rates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }

    try {
      await axios.post(`${API}/expense-categories`, {
        name: newCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Category created successfully');
      setNewCategory('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const addManualItem = () => {
    if (!newItem.item_name || newItem.quantity <= 0 || newItem.price <= 0) {
      toast.error('Please fill in all item details');
      return;
    }

    const item = {
      item_name: newItem.item_name,
      description: newItem.description,
      quantity: newItem.quantity,
      unit: newItem.unit,
      price: newItem.price,
      total: newItem.quantity * newItem.price
    };

    setPOFormData({
      ...poFormData,
      items: [...poFormData.items, item]
    });

    // Reset form
    setNewItem({
      item_name: '',
      description: '',
      quantity: 1,
      unit: 'pcs',
      price: 0
    });
  };

  const removePOItem = (index) => {
    setPOFormData({
      ...poFormData,
      items: poFormData.items.filter((_, i) => i !== index)
    });
  };

  const calculatePOTotals = () => {
    // Calculate totals WITHOUT currency conversion
    // We store amounts as entered, conversion only happens when pushing to expenses
    const subtotal = poFormData.items.reduce((sum, item) => sum + item.total, 0);
    const taxAmount = subtotal * (poFormData.tax / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const getPOCurrencySymbol = () => {
    if (poFormData.currency === 'USD') return '$';
    if (poFormData.currency === 'MVR') return 'ރ';
    if (poFormData.currency === 'CHN') return '¥';
    return poFormData.currency + ' ';
  };

  const handleEditPO = (po) => {
    setEditingPO(po);
    setPOFormData({
      supplier_name: po.supplier_name,
      date: new Date(po.date).toISOString().split('T')[0],
      items: po.items,
      category_id: po.category_id,
      notes: po.notes || '',
      tax: po.tax,
      currency: po.currency,
      advance_paid: po.advance_paid || 0
    });
    setShowPODialog(true);
  };

  const handleSubmitPO = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (poFormData.items.length === 0) {
      toast.error('Please add at least one item');
      setLoading(false);
      return;
    }

    if (!poFormData.category_id) {
      toast.error('Please select an expense category');
      setLoading(false);
      return;
    }

    try {
      const { subtotal, total } = calculatePOTotals();
      
      const poData = {
        supplier_name: poFormData.supplier_name,
        date: new Date(poFormData.date).toISOString(),
        items: poFormData.items,
        subtotal,
        tax: poFormData.tax,
        total,
        advance_paid: parseFloat(poFormData.advance_paid) || 0,
        currency: poFormData.currency,
        category_id: poFormData.category_id,
        notes: poFormData.notes
      };

      if (editingPO) {
        // Update existing PO
        await axios.put(`${API}/purchase-orders/${editingPO.id}`, poData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order updated successfully');
      } else {
        // Create new PO
        await axios.post(`${API}/purchase-orders`, poData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order created successfully');
      }

      setShowPODialog(false);
      resetPOForm();
      setEditingPO(null);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingPO ? 'update' : 'create'} purchase order`);
    } finally {
      setLoading(false);
    }
  };

  const completePO = async (poId) => {
    console.log('completePO function called with ID:', poId);
    
    // Direct push to expenses without confirmation
    const loadingToast = toast.loading('Pushing to expenses...');
    
    try {
      const response = await axios.put(`${API}/purchase-orders/${poId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Complete PO response:', response.data);
      toast.dismiss(loadingToast);
      toast.success(`✓ PO pushed to expenses! Expense ID: ${response.data.expense_id}`);
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Complete PO error:', error);
      toast.dismiss(loadingToast);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed';
      toast.error(`Failed: ${errorMessage}`);
    }
  };

  const deletePO = async (poId) => {
    console.log('deletePO function called with ID:', poId);
    
    // Direct delete without confirmation  
    const loadingToast = toast.loading('Deleting...');
    
    try {
      await axios.delete(`${API}/purchase-orders/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Delete PO success');
      toast.dismiss(loadingToast);
      toast.success('✓ PO deleted');
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Delete PO error:', error);
      toast.dismiss(loadingToast);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete';
      toast.error(`Failed: ${errorMessage}`);
    }
  };

  const resetPOForm = () => {
    setPOFormData({
      supplier_name: '',
      date: new Date().toISOString().split('T')[0],
      items: [],
      category_id: '',
      notes: '',
      tax: 0,
      currency: 'USD',
      advance_paid: 0
    });
    setEditingPO(null);
  };

  // Calculate total POs in USD (convert currencies)
  const totalPOs = purchaseOrders.reduce((sum, po) => {
    let amountInUSD = po.total;
    
    // Convert to USD if not already
    if (po.currency !== 'USD') {
      const rate = exchangeRates.find(r => r.currency === po.currency);
      if (rate && rate.rate_to_usd) {
        amountInUSD = po.total / rate.rate_to_usd;
      }
    }
    
    return sum + amountInUSD;
  }, 0);
  
  const pendingPOs = purchaseOrders.filter(po => po.status === 'pending' || po.status === 'partially_paid').length;

  const handleExportPDF = () => {
    const rows = [];
    const posToExport = activeTab === 'all' ? purchaseOrders : purchaseOrders.filter(po => po.status === activeTab);
    posToExport.forEach((po, pi) => {
      const poCurrency = po.currency || 'USD';
      const rate = exchangeRates.find(r => r.currency === poCurrency);
      const toUSD = (amt) => poCurrency === 'USD' ? amt : (rate ? amt / rate.rate_to_usd : amt);
      (po.items || []).forEach((item) => {
        const itemTotal = item.total || 0;
        const priceStr = poCurrency === 'USD'
          ? `$${fmtNum(item.price || 0)}`
          : `${poCurrency} ${fmtNum(item.price || 0)} ($${fmtNum(toUSD(item.price || 0))})`;
        const totalStr = poCurrency === 'USD'
          ? `$${fmtNum(itemTotal)}`
          : `${poCurrency} ${fmtNum(itemTotal)} ($${fmtNum(toUSD(itemTotal))})`;
        rows.push([
          rows.length + 1,
          new Date(po.date).toLocaleDateString(),
          po.po_number || `PO-${pi + 1}`,
          po.supplier_name || '-',
          item.item_name || '-',
          priceStr,
          item.quantity || 0,
          totalStr
        ]);
      });
    });
    const totalOriginal = posToExport.reduce((s, po) => s + (po.total || 0), 0);
    const totalUSD = posToExport.reduce((s, po) => {
      const poCurrency = po.currency || 'USD';
      if (poCurrency === 'USD') return s + po.total;
      const rate = exchangeRates.find(r => r.currency === poCurrency);
      return s + (rate ? po.total / rate.rate_to_usd : po.total);
    }, 0);
    exportListPDF({
      title: `Purchase Orders${activeTab !== 'all' ? ` — ${activeTab.replace('_', ' ').toUpperCase()}` : ''}`,
      headers: ['#', 'Date', 'PO#', 'Supplier', 'Item Name', 'Price', 'Qty', 'Total'],
      rows,
      totalLabel: 'Grand Total (USD)',
      totalValue: `$${fmtNum(totalUSD)}`,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Purchase Orders</h1>
            <p className="text-slate-600 mt-1">
              Total: {formatCurrency(totalPOs)} | {pendingPOs} pending | {purchaseOrders.length} total
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleExportPDF} data-testid="po-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Tag className="w-4 h-4 mr-2" />
                  Manage Categories
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Expense Categories ({categories.length})</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="New category name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                    />
                    <Button onClick={handleCreateCategory}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="flex justify-between items-center p-3 bg-slate-50 rounded-lg"
                      >
                        <span className="font-medium">{category.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                    {categories.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-slate-500">No categories yet</p>
                        <p className="text-sm text-slate-400 mt-1">Create your first category above</p>
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {canCreatePO && (
              <Dialog open={showPODialog} onOpenChange={(open) => {
                setShowPODialog(open);
                if (!open) resetPOForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Purchase Order
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitPO} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Supplier *</Label>
                      <select
                        value={poFormData.supplier_name}
                        onChange={(e) => setPOFormData({ ...poFormData, supplier_name: e.target.value })}
                        className="w-full border rounded-md px-3 py-2"
                        required
                      >
                        <option value="">Select supplier</option>
                        {suppliers.map((supplier) => (
                          <option key={supplier.id} value={supplier.name}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        <a href="/suppliers" className="text-blue-600 hover:underline">
                          + Add new supplier
                        </a>
                      </p>
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={poFormData.date}
                        onChange={(e) => setPOFormData({ ...poFormData, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Expense Category *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCategoryDialog(true)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        New Category
                      </Button>
                    </div>
                    {categories.length === 0 ? (
                      <div className="p-4 border rounded-lg bg-yellow-50 text-yellow-800 text-sm">
                        <p className="font-semibold">No categories available</p>
                        <p className="mt-1">Please create a category first using the "New Category" button above.</p>
                      </div>
                    ) : (
                      <select
                        value={poFormData.category_id}
                        onChange={(e) => setPOFormData({ ...poFormData, category_id: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      >
                        <option value="">Select category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <Label className="font-semibold text-base mb-3">Add Items Manually</Label>
                    
                    {/* Add Item Form */}
                    <div className="border rounded-lg p-4 bg-slate-50 space-y-3 mb-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Item Name *</Label>
                          <Input
                            value={newItem.item_name}
                            onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                            placeholder="e.g., Office Supplies"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={newItem.description}
                            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                            placeholder="Optional details"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Quantity *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={newItem.quantity}
                            onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 1 })}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Unit</Label>
                          {addingUnit ? (
                            <div className="flex gap-1 mt-0.5">
                              <input
                                type="text"
                                value={newUnitName}
                                onChange={(e) => setNewUnitName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && newUnitName.trim()) {
                                    const val = newUnitName.trim().toLowerCase().replace(/\s+/g, '_');
                                    setCustomUnits(prev => [...prev, { value: val, label: newUnitName.trim() }]);
                                    setNewItem({ ...newItem, unit: val });
                                    setNewUnitName('');
                                    setAddingUnit(false);
                                  }
                                }}
                                placeholder="Unit name"
                                className="flex-1 px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                autoFocus
                                data-testid="custom-unit-input"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (newUnitName.trim()) {
                                    const val = newUnitName.trim().toLowerCase().replace(/\s+/g, '_');
                                    setCustomUnits(prev => [...prev, { value: val, label: newUnitName.trim() }]);
                                    setNewItem({ ...newItem, unit: val });
                                    setNewUnitName('');
                                  }
                                  setAddingUnit(false);
                                }}
                                className="px-2 py-1.5 bg-emerald-600 text-white rounded-md text-xs hover:bg-emerald-700"
                                data-testid="custom-unit-save"
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                onClick={() => { setAddingUnit(false); setNewUnitName(''); }}
                                className="px-2 py-1.5 border border-slate-300 rounded-md text-xs hover:bg-slate-50"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <select
                              value={newItem.unit}
                              onChange={(e) => {
                                if (e.target.value === '__add_new__') {
                                  setAddingUnit(true);
                                } else {
                                  setNewItem({ ...newItem, unit: e.target.value });
                                }
                              }}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                              data-testid="po-unit-select"
                            >
                              {DEFAULT_UNITS.map(u => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                              {customUnits.map(u => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                              <option value="__add_new__">+ Add Unit...</option>
                            </select>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Price *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newItem.price}
                            onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                            className="text-sm"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            type="button"
                            onClick={addManualItem}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Items List */}
                    <div className="border rounded-lg p-4 space-y-3">
                      {poFormData.items.map((item, index) => (
                        <div key={index} className="flex gap-3 items-center bg-white p-3 rounded border">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{item.item_name}</p>
                            {item.description && <p className="text-xs text-slate-500">{item.description}</p>}
                          </div>
                          <div className="text-sm text-slate-600">
                            {item.quantity} {item.unit}
                          </div>
                          <div className="text-sm text-slate-600 w-20 text-right">
                            ${fmtNum(item.price)}
                          </div>
                          <div className="text-sm font-bold text-slate-900 w-24 text-right">
                            ${fmtNum(item.total)}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removePOItem(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      ))}
                      {poFormData.items.length === 0 && (
                        <p className="text-center text-slate-500 text-sm py-4">No items added yet</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-4 gap-4 border-t pt-4">
                    <div>
                      <Label>Currency *</Label>
                      <select
                        value={poFormData.currency}
                        onChange={(e) => setPOFormData({ ...poFormData, currency: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="USD">USD ($)</option>
                        {exchangeRates.map((rate) => (
                          <option key={rate.currency} value={rate.currency}>
                            {rate.currency} ({rate.currency === 'MVR' ? 'ރ' : rate.currency === 'CHN' ? '¥' : rate.currency})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Tax (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={poFormData.tax}
                        onChange={(e) => setPOFormData({ ...poFormData, tax: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <div className="bg-slate-50 p-4 rounded space-y-2">
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span className="font-semibold flex items-center">
                            <CurrencySymbol currency={poFormData.currency} />
                            {fmtNum(calculatePOTotals().subtotal)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Tax:</span>
                          <span className="font-semibold flex items-center">
                            <CurrencySymbol currency={poFormData.currency} />
                            {fmtNum(calculatePOTotals().taxAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t pt-2">
                          <span>Total:</span>
                          <span className="text-emerald-600 flex items-center">
                            <CurrencySymbol currency={poFormData.currency} />
                            {fmtNum(calculatePOTotals().total)}
                          </span>
                        </div>
                        {poFormData.currency !== 'USD' && (
                          <div className="text-xs text-slate-500 text-right pt-1">
                            ≈ $ {(calculatePOTotals().total / (exchangeRates.find(r => r.currency === poFormData.currency)?.rate_to_usd || 1))} USD
                          </div>
                        )}
                        {parseFloat(poFormData.advance_paid || 0) > 0 && (
                          <>
                            <div className="flex justify-between text-sm text-blue-600 border-t pt-2">
                              <span>Advance Paid:</span>
                              <span className="font-semibold flex items-center">
                                <CurrencySymbol currency={poFormData.currency} />
                                {fmtNum(parseFloat(poFormData.advance_paid || 0))}
                              </span>
                            </div>
                            <div className="flex justify-between text-lg font-bold text-orange-600">
                              <span>Unpaid Balance:</span>
                              <span className="flex items-center">
                                <CurrencySymbol currency={poFormData.currency} />
                                {fmtNum(calculatePOTotals().total - parseFloat(poFormData.advance_paid || 0))}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Advance Paid (Optional)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max={calculatePOTotals().total}
                      value={poFormData.advance_paid}
                      onChange={(e) => setPOFormData({ ...poFormData, advance_paid: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Enter any advance payment made for this purchase order
                    </p>
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <textarea
                      className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md"
                      value={poFormData.notes}
                      onChange={(e) => setPOFormData({ ...poFormData, notes: e.target.value })}
                      placeholder="Additional notes..."
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? (editingPO ? 'Updating...' : 'Creating...') : (editingPO ? 'Update Purchase Order' : 'Create Purchase Order')}
                    </Button>
                    
                    {editingPO && canEditPO && !editingPO.pushed_to_expense && (
                      <Button
                        type="button"
                        onClick={async () => {
                          // First update the PO
                          await handleSubmitPO({ preventDefault: () => {} });
                          // Then complete it
                          setTimeout(() => {
                            completePO(editingPO.id);
                            setShowPODialog(false);
                            setEditingPO(null);
                          }, 500);
                        }}
                        disabled={loading}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        Update & Complete
                      </Button>
                    )}
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Search & Filter Tabs */}
        <div className="space-y-3">
          <SearchFilter
            data={activeTab === 'all' ? purchaseOrders : purchaseOrders.filter(po => po.status === activeTab)}
            onFilter={setFilteredPOs}
            searchFields={['supplier_name', 'po_number', 'notes', 'category_name']}
            placeholder="Search by supplier, PO#, category..."
          />
          <div className="flex gap-1 border-b" data-testid="po-status-tabs">
            {STATUS_TABS.map(tab => {
              const count = tab.value === 'all' ? purchaseOrders.length : purchaseOrders.filter(po => po.status === tab.value).length;
              return (
                <button key={tab.value}
                  onClick={() => {
                    setActiveTab(tab.value);
                    const base = tab.value === 'all' ? purchaseOrders : purchaseOrders.filter(po => po.status === tab.value);
                    setFilteredPOs(base);
                  }}
                  data-testid={`po-tab-${tab.value}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.value
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}>
                  {tab.label} {count > 0 && <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Purchase Orders List */}
        <div className="grid gap-4">
          {filteredPOs.length === 0 ? (
            <Card className="p-12 text-center">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{purchaseOrders.length === 0 ? 'No purchase orders yet' : 'No purchase orders match filters'}</p>
            </Card>
          ) : filteredPOs.map((po) => {
            // Get currency for this PO
            const poCurrency = po.currency || 'USD';
            
            return (
            <Card key={po.id} className="p-6 hover:shadow-lg transition-all">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <ShoppingCart className="w-5 h-5 text-slate-600" />
                    <h3 className="text-xl font-bold text-slate-900">{po.po_number}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      po.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      po.status === 'partially_paid' ? 'bg-blue-100 text-blue-700' :
                      po.status === 'paid' ? 'bg-green-100 text-green-700' :
                      po.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {po.status === 'partially_paid' ? 'PARTIALLY PAID' : po.status.toUpperCase()}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                      {po.category_name}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                      {poCurrency}
                    </span>
                  </div>
                  <p className="text-slate-700 font-semibold">Supplier: {po.supplier_name}</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Date: {new Date(po.date).toLocaleDateString()} | {po.items.length} items
                  </p>
                  {po.notes && <p className="text-sm text-slate-600 mt-1">Notes: {po.notes}</p>}
                  {po.pushed_to_expense && (
                    <p className="text-sm text-emerald-600 mt-2 font-semibold">
                      ✓ Pushed to Expenses: {po.expense_id}
                    </p>
                  )}
                  
                  {/* Items list */}
                  <div className="mt-3 space-y-1">
                    {po.items.map((item, idx) => (
                      <div key={idx} className="text-sm text-slate-600 flex justify-between">
                        <span>• {item.item_name || item.product_name} {item.description && `(${item.description})`} x {item.quantity} {item.unit || ''}</span>
                        <span className="font-mono flex items-center">
                          <CurrencySymbol currency={poCurrency} />
                          {fmtNum(item.total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <div>
                    <p className="text-3xl font-bold text-blue-600 flex items-center justify-end">
                      <CurrencySymbol currency={poCurrency} />
                      {fmtNum(po.total)}
                    </p>
                    {po.advance_paid > 0 && (
                      <>
                        <p className="text-sm text-slate-500 line-through">Advance: {fmtNum(po.advance_paid)}</p>
                        <p className="text-lg font-semibold text-orange-600 flex items-center justify-end">
                          <CurrencySymbol currency={poCurrency} />
                          {fmtNum(po.unpaid_balance)}
                        </p>
                        <p className="text-xs text-slate-500">Unpaid Balance</p>
                      </>
                    )}
                    <p className="text-sm text-slate-500 mt-1">{poCurrency}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewingPO(po);
                      }}
                      variant="outline"
                      size="sm"
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {!po.pushed_to_expense && canEditPO && (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPO(po);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-purple-600 hover:bg-purple-50"
                      >
                        <Tag className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    {po.status === 'pending' && !po.pushed_to_expense && canEditPO && (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          completePO(po.id);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-emerald-600 hover:bg-emerald-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Complete
                      </Button>
                    )}
                    {!po.pushed_to_expense && canDelete && (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deletePO(po.id);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
            );
          })}
        </div>

        {/* PO Viewer */}
        {viewingPO && (
          <POViewer po={viewingPO} onClose={() => setViewingPO(null)} />
        )}
      </div>
    </Layout>
  );
}
